// @flow
import React, { useEffect, useMemo, useState } from 'react';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import ProgressTracker from '@jexo/progress-tracker';
import {
    getJiraIssuesCountGroupedByStatusCategoryId,
    getJiraIssuesRelatedToComponentJql,
} from '../../services/jira-services';
import { ISSUE_STATUS_CATEGORY_IDS } from '../../utils/consts';

type Props = {
    releaseName: string,
};

const IssuesProgressTracker = ({ releaseName }: Props) => {
    const [isLoading, setIsLoading] = useState(true);
    const [issuesCountByStatusCategoryId, setIssuesCountByStatusCategoryId] = useState({});

    useEffect(() => {
        setIsLoading(true);
        (async () => {
            setIssuesCountByStatusCategoryId(
                await getJiraIssuesCountGroupedByStatusCategoryId({
                    jql: getJiraIssuesRelatedToComponentJql({
                        componentName: releaseName,
                        issuePropertyKey: 'Component Versions',
                    }),
                }),
            );
            setIsLoading(false);
        })();
    }, [releaseName]);

    const totalIssueCount = useMemo(
        () => Object.values(issuesCountByStatusCategoryId).reduce((acc, curr) => acc + curr, 0),
        [issuesCountByStatusCategoryId],
    );

    const segments = useMemo(() => {
        const done = issuesCountByStatusCategoryId[ISSUE_STATUS_CATEGORY_IDS.DONE] ?? 0;
        const inProgress = issuesCountByStatusCategoryId[ISSUE_STATUS_CATEGORY_IDS.IN_PROGRESS] ?? 0;
        const toDo = issuesCountByStatusCategoryId[ISSUE_STATUS_CATEGORY_IDS.TO_DO] ?? 0;

        return [
            {
                proportionComplete: done / totalIssueCount,
                color: colors.G400,
                label: `${done} Done`,
            },
            {
                proportionComplete: inProgress / totalIssueCount,
                color: colors.B400,
                label: `${inProgress} In progress`,
            },
            {
                proportionComplete: toDo / totalIssueCount,
                color: colors.N40,
                label: `${toDo} Not started`,
            },
        ];
    }, [issuesCountByStatusCategoryId, totalIssueCount]);

    if (isLoading) {
        return <Spinner />;
    }

    if (totalIssueCount === 0) {
        return <div>No issues</div>;
    }

    return <ProgressTracker segments={segments} />;
};

export default IssuesProgressTracker;
